import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import 'moment-timezone';
import { manualCashLimitPropertiesType } from '../../types';
import ExpandedListTab from '../ExpandedListTab';
import SaveButton from '../buttons/SaveButton';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import {
    ALL_MANUAL_CASH_LIMITS_QUERY_NAME,
    CREATE_MANUAL_CASH_LIMIT,
} from '../../graphql/manualCashLimits';
import useMultiValidator from '../../hooks/useMultiValidator';
import useExtendedState from '../../hooks/useExtendedState';
import SelectField from '../fields/SelectField';
import TextField from '../fields/TextField';
import {
    MANUAL_CASH_LIMIT_DESCRIPTION_MAX_LENGTH,
    manualCashLimitStartDateValidator,
    manualCashLimitEndDateValidator,
    manualCashLimitDescriptionValidator,
    manualCashLimitValueValidator,
} from '../../validators/manualCashLimits';
import NumberField from '../fields/NumberField';
import {
    date,
    toLocalDate,
} from '../../utils';
import DateField from '../fields/DateField';
import { removeCopyOfManualCashLimitData } from '../../actions/copyOfManualCashLimit';
import Constants from '../../utils/Constants';
import useSingleValidator from '../../hooks/useSingleValidator';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

export const currentDateInTimeZone = (timeZoneID) => date(null, (newDate) => {
    const timeZoneOffset = moment().tz(timeZoneID).utcOffset();
    const clientTimeZoneOffset = moment().utcOffset();
    newDate.setMinutes(newDate.getMinutes() + timeZoneOffset - clientTimeZoneOffset);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
});

ManualCashLimitCreateTab.propTypes = {
    manualCashLimitProps: manualCashLimitPropertiesType.isRequired,
    hideHandler: PropTypes.func.isRequired,
    copyToCreate: PropTypes.shape({
        memberId: PropTypes.string,
        clearingHouseCode: PropTypes.string,
        currencyCode: PropTypes.string,
        limitValue: PropTypes.number,
        beginDate: PropTypes.string,
        endDate: PropTypes.string,
        description: PropTypes.string,
    }),
};

ManualCashLimitCreateTab.defaultProps = {
    copyToCreate: null,
};

function ManualCashLimitCreateTab(props) {
    const {
        manualCashLimitProps,
        hideHandler,
        copyToCreate,
    } = props;
    const { t } = useTranslation();
    const {
        availableMembers,
        availableClearingHouses,
        availableCurrencies,
        minCashLimit,
        maxCashLimit,
    } = manualCashLimitProps;
    if (copyToCreate && !copyToCreate.beginDate) {
        copyToCreate.beginDate = date(copyToCreate.beginDate);
    }
    const dispatch = useDispatch();
    const memberIdState = useExtendedState(availableMembers[0].memberId);
    const [invalidBeforeDate, setInvalidBeforeDate] = useState(currentDateInTimeZone(availableClearingHouses[0].timeZone.ID));
    const [memberName, setMemberName] = useState(availableMembers[0].name);
    useEffect(() => {
        setMemberName(availableMembers.find((availableMember) => availableMember.memberId === memberIdState.value).name);
    }, [memberIdState, availableMembers]);
    const setMember = useCallback((newMemberId) => {
        setMemberName(availableMembers.find((availableMember) => availableMember.memberId === newMemberId).name);
    }, [availableMembers]);
    const clearingHouseCodeState = useExtendedState(availableClearingHouses[0].code);
    const currencyCodeState = useExtendedState(availableCurrencies[0].code);
    const beginDateState = useExtendedState(date(null, (newDate) => newDate.setHours(0, 0, 0, 0)));
    const endDateState = useExtendedState('');
    const handleNewBeginDate = (newBeginDate) => {
        if (newBeginDate instanceof Date) {
            const beginDate = beginDateState.value;
            const endDate = endDateState.value;
            if (beginDateState.value instanceof Date && endDateState.value instanceof Date) {
                const newEndDate = date(newBeginDate, (d) => {
                    const hoursDiff = endDate >= beginDate
                        ? Math.abs(endDate - beginDate) / 36e5
                        : 0;
                    d.setHours(d.getHours() + hoursDiff);
                });
                endDateState.setValue(newEndDate);
            } else if (endDate instanceof Date && endDate < newBeginDate) {
                endDateState.setValue(date(newBeginDate));
            }
        }
    };
    const limitValueState = useExtendedState(copyToCreate ? copyToCreate.limitValue : 1);
    const descriptionState = useExtendedState(copyToCreate ? copyToCreate.description : '');
    const {
        validator: beginDateValidator,
        isValid: isBeginDateValid,
        reset: resetBeginDateValidator,
    } = useSingleValidator(true, useMemo(() => manualCashLimitStartDateValidator(t, invalidBeforeDate), [t, invalidBeforeDate]));
    const {
        validator: endDateValidator,
        isValid: isEndDateValid,
        reset: resetEndDateValidator,
    } = useSingleValidator(true, useMemo(() => manualCashLimitEndDateValidator(t, beginDateState.value, invalidBeforeDate), [t, beginDateState.value, invalidBeforeDate]));
    const onClearingHouseChange = (newClearingHouseCode) => {
        const newClearingHouse = availableClearingHouses.find((availableClearingHouse) => availableClearingHouse.code === newClearingHouseCode);
        setInvalidBeforeDate(currentDateInTimeZone(newClearingHouse.timeZone.ID));
        resetBeginDateValidator();
        resetEndDateValidator();
    };
    const { validators, isValid } = useMultiValidator(true, {
        limitValue: manualCashLimitValueValidator(t, minCashLimit, maxCashLimit),
        description: compositeValidator(manualCashLimitDescriptionValidator(t), wafValidator(t)),
    });
    const [isDisabled, setIsDisabled] = useState(false);
    Promise.all([isValid(), isBeginDateValid(), isEndDateValid()])
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));

    const extendedMutation = useExtendedMutation(CREATE_MANUAL_CASH_LIMIT, {
        variables: {
            memberId: memberIdState.value,
            clearingHouseCode: clearingHouseCodeState.value,
            currencyCode: currencyCodeState.value,
            beginDate: toLocalDate(beginDateState.value),
            endDate: toLocalDate(endDateState.value),
            limitValue: limitValueState.value,
            description: descriptionState.value,
        },
        successCallback: hideHandler,
        successMessageOptions: { key: 'manual-cash-limits.notification.manual-cash-limit-create-success', dataProperty: 'createManualCashLimit' },
        failureMessageOptions: { key: 'manual-cash-limits.notification.manual-cash-limit-create-failure', params: { memberName, memberId: memberIdState.value } },
        refetchQueries: [ALL_MANUAL_CASH_LIMITS_QUERY_NAME],
    });
    useEffect(() => {
        if (copyToCreate) {
            if (availableMembers.find((availableMember) => availableMember.memberId === copyToCreate.memberId)) {
                memberIdState.setValueIfNew(copyToCreate.memberId);
            }
            if (availableClearingHouses.find((availableClearingHouse) => availableClearingHouse.code === copyToCreate.clearingHouseCode)) {
                clearingHouseCodeState.setValueIfNew(copyToCreate.clearingHouseCode);
            }
            if (availableCurrencies.find((availableCurrency) => availableCurrency.code === copyToCreate.currencyCode)) {
                currencyCodeState.setValueIfNew(copyToCreate.currencyCode);
            }
            limitValueState.setValueIfNew(copyToCreate.limitValue);
            beginDateState.setValueIfNew(date(copyToCreate.beginDate));
            endDateState.setValueIfNew(copyToCreate.endDate ? date(copyToCreate.endDate) : '');
            descriptionState.setValueIfNew(copyToCreate.description || '');
        }
        return () => {
            dispatch(removeCopyOfManualCashLimitData());
        };
    }, [
        copyToCreate,
        setMember,
        availableMembers,
        clearingHouseCodeState,
        availableClearingHouses,
        currencyCodeState,
        availableCurrencies,
        limitValueState,
        beginDateState,
        endDateState,
        descriptionState,
        dispatch,
        memberIdState,
    ]);
    return (
        <ExpandedListTab
            className="manual-cash-limit-create-tab"
            title={t('manual-cash-limits.manual-cash-limit-detail')}
        >
            <div className="rich-list-row-expanded--fields">
                <SelectField
                    label={t('manual-cash-limits.member')}
                    state={memberIdState}
                    options={availableMembers.map((availableMember) => (
                        <option value={availableMember.memberId} key={availableMember.memberId}>{availableMember.name} ({availableMember.memberId})</option>
                    ))}
                    className="manual-cash-limit-create-tab--fields--member"
                    inputId="manual-cash-limit-create-tab--fields--member"
                    isRequired
                />
                <SelectField
                    label={t('manual-cash-limits.clearing-house')}
                    state={clearingHouseCodeState}
                    onChange={onClearingHouseChange}
                    options={availableClearingHouses.map((availableClearingHouse) => (
                        <option value={availableClearingHouse.code} key={availableClearingHouse.code}>{availableClearingHouse.name} ({availableClearingHouse.code})</option>
                    ))}
                    className="manual-cash-limit-create-tab--fields--clearing-house"
                    inputId="manual-cash-limit-create-tab--fields--clearing-house"
                    isRequired
                />
                <SelectField
                    label={t('manual-cash-limits.currency')}
                    state={currencyCodeState}
                    options={availableCurrencies.map((availableCurrency) => (
                        <option value={availableCurrency.code} key={availableCurrency.code}>{availableCurrency.name} ({availableCurrency.code})</option>
                    ))}
                    className="manual-cash-limit-create-tab--fields--currency"
                    inputId="manual-cash-limit-create-tab--fields--currency"
                    isRequired
                />
                <NumberField
                    label={t('manual-cash-limits.limit-value')}
                    className="manual-cash-limit-create-tab--fields--limit-value"
                    inputId="manual-cash-limit-create-tab--fields--limit-value"
                    state={limitValueState}
                    fractionDigits={2}
                    validator={validators.limitValue}
                    step={Constants.DEFAULT_LIMIT_VALUE_STEP}
                    decrementDisabled={limitValueState.value - Constants.DEFAULT_LIMIT_VALUE_STEP < minCashLimit}
                    incrementDisabled={limitValueState.value + Constants.DEFAULT_LIMIT_VALUE_STEP > maxCashLimit}
                    min={minCashLimit}
                    max={maxCashLimit}
                    isRequired
                />
                <DateField
                    label={t('manual-cash-limits.begin-date')}
                    state={beginDateState}
                    onChange={handleNewBeginDate}
                    className="manual-cash-limit-create-tab--fields--begin-date"
                    inputId="manual-cash-limit-create-tab--fields--end-date"
                    invalidBefore={invalidBeforeDate}
                    validator={beginDateValidator}
                    validateOnValidatorChange
                    isRequired
                />
                <DateField
                    label={t('manual-cash-limits.end-date')}
                    state={endDateState}
                    className="manual-cash-limit-create-tab--fields--end-date"
                    inputId="manual-cash-limit-create-tab--fields--end-date"
                    invalidBefore={beginDateState.value}
                    validator={endDateValidator}
                    validateOnValidatorChange
                />
                <TextField
                    label={t('manual-cash-limits.description')}
                    state={descriptionState}
                    className="manual-cash-limit-create-tab--fields--description x-wide"
                    inputId="manual-cash-limit-create-tab--fields--description"
                    validator={validators.description}
                    maxLength={MANUAL_CASH_LIMIT_DESCRIPTION_MAX_LENGTH}
                    placeholder={t('manual-cash-limits.placeholder.description')}
                />
            </div>
            <div className="rich-list-row-expanded--buttons">
                <SaveButton
                    extendedMutation={extendedMutation}
                    disabled={isDisabled}
                    confirmationMessage={t('manual-cash-limits.confirm-create-manual-cash-limit', { memberName, memberId: memberIdState.value })}
                    extraConfirmationMessage={t('manual-cash-limits.extra-confirm-create-manual-cash-limit', { memberName, memberId: memberIdState.value })}
                />
            </div>
        </ExpandedListTab>
    );
}

export default ManualCashLimitCreateTab;
